import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Message } from '@pacer/shared';
import * as api from '../api';
import { Icon } from './Icon';
import { Avatar, Card, IconButton, cx } from './ui';
import { queryKeys } from '../state/queries';
import { useActiveMemberId } from '../state/activeMember';

/**
 * The family board — the fridge sticky-note, on the wall. Lives on the dashboard rather than
 * behind a nav tab because a note you have to go looking for is a note nobody reads. Pinned
 * messages float to the top here and also show on the screensaver.
 */
export function MessageBoard() {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const [text, setText] = useState('');

  const { data: messages = [] } = useQuery({ queryKey: queryKeys.messages, queryFn: api.getMessages });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.messages });

  const post = useMutation({
    mutationFn: (body: string) => api.postMessage(body, activeId),
    onSuccess: async () => {
      setText('');
      await refresh();
    },
  });
  const pin = useMutation({
    mutationFn: (m: Message) => api.updateMessage(m.id, { pinned: !m.pinned }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteMessage(id), onSuccess: refresh });

  const submit = (): void => {
    const t = text.trim();
    if (t) post.mutate(t);
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="message" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Board</h2>
      </div>

      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Leave a note…"
          enterKeyHint="send"
          autoCapitalize="sentences"
          className="touch flex-1 rounded-md border border-border bg-surface-high px-3 text-[16px] text-primary placeholder:text-muted focus:border-accent focus:outline-none"
        />
        <IconButton icon="plus" label="Post note" variant="primary" onClick={submit} disabled={!text.trim()} />
      </div>

      {messages.length > 0 && (
        <div className="flex flex-col gap-2">
          {messages.map((m) => {
            const author = members.find((x) => x.id === m.authorId);
            return (
              <Card
                key={m.id}
                className={cx(
                  'group relative flex gap-3 px-3 py-2.5',
                  m.pinned && 'border-l-2 border-l-accent',
                )}
              >
                {author && <Avatar name={author.name} color={author.color} photoUrl={author.avatarUrl} size={26} />}
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] break-words whitespace-pre-wrap text-primary">{m.body}</p>
                  <p className="mono mt-0.5 font-mono text-[11px] text-text-sub">
                    {[author?.name, DateTime.fromISO(m.createdAt).toRelative({ style: 'short' })]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 items-start gap-0.5">
                  <IconButton
                    icon="pin"
                    label={m.pinned ? 'Unpin' : 'Pin'}
                    size="sm"
                    onClick={() => pin.mutate(m)}
                    className={cx(m.pinned ? 'text-accent' : 'text-muted opacity-0 group-hover:opacity-100')}
                  />
                  <IconButton
                    icon="trash"
                    label="Delete note"
                    size="sm"
                    onClick={() => remove.mutate(m.id)}
                    className="text-muted opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
